import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  styled,
  CssBaseline,
  Box,
  AppBar,
  Toolbar,
  Typography,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
} from "@mui/material";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import KeyIcon from "@mui/icons-material/Key";
import PersonIcon from "@mui/icons-material/Person";
import MailIcon from "@mui/icons-material/Mail";
import SpaceDashboardIcon from "@mui/icons-material/SpaceDashboard";
import { DownOutlined } from "@ant-design/icons";
import { ConfigProvider, Tree } from "antd";
import {
  logout,
  clearFirstLogin,
  setUserData,
  changeBranch,
} from "../redux/features/auth/authSlice";
import {
  Routes,
  Route,
  useLocation,
  Navigate,
  useNavigate,
} from "react-router-dom";

import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import dayjs from "dayjs";
// import api
import userApi from "../api/userApi";
import invoiceApi from "../api/invoiceApi";
// import pages
import InvoicePage from "../pages/InvoicePage";
import HandleInvoiceErrorPage from "../pages/HandleInvoiceErrorPage";
import TaxReportPage from "../pages/TaxReportPage";
import AnalyzePage from "../pages/AnalyzePage";
import AccountPage from "../pages/AccountPage";
import CustomerInforPage from "../pages/CustomerInforPage";
import UsersPage from "../pages/UsersPage";
import RolePage from "../pages/RolePage";
import InvoiceSignAndTemplatePage from "../pages/InvoiceSignAndTemplatePage/InvoiceSignAndTemplatePage";
import InvoiceRegistrationPage from "../pages/InvoiceRegistrationPage";
import DataConfigPage from "../pages/DataConfigPage";
import WidgetConfigPage from "../pages/WidgetConfigPage";
import RelisoftPage from "../pages/RelisoftPage";
import CustomersPage from "../pages/CustomersPage";
import OrdersPage from "../pages/OrdersPage";
import PriceListPage from "../pages/PriceListPage";
import SaleReportPage from "../pages/SaleReportPage";
import SystemConfigPage from "../pages/SystemConfigPage";
import AutoSalePage from "../pages/AutoSalePage";
import DevPage from "../pages/DevPage";
import InvoiceSupplierConnectionPage from "../pages/InvoiceSupplierConnectionPage";
import PaymentMethodPage from "../pages/PaymentMethodPage";
import InvoiceSummaryReportPage from "../pages/InvoiceSummaryReportPage";
import ProductTablePage from "../pages/ProductTablePage";
import IndustryPage from "../pages/IndustryPage";
import SalesRevenueReportPage from "../pages/SalesRevenueReportPage";
import BusinessesPage from "../pages/BusinessesPage";
import TaxPolicyPage from "../pages/TaxPolicyPage";
import TaxSettingsPage from "../pages/TaxSettingsPage";
import StorageReportPage from "../pages/StorageReportPage";
import IndustryChoicePage from "../pages/IndustryChoicePage";
import InvoiceReconciliationReportPage from "../pages/InvoiceReconciliationReportPage";

import ConfirmDialog from "../components/ConfirmDialog";
import InformationDialog from "../components/InformationDialog";
import customerApi from "../api/customerApi";

const drawerWidth = 240;

const AppBarStyled = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  backgroundColor: "#E8E8E8",
  color: "black",
}));

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    flexGrow: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: drawerWidth,
    }),
    overflowX: "hidden",
  }),
);
const pageMap = {
  InvoicePage,
  HandleInvoiceErrorPage,
  TaxReportPage,
  AnalyzePage,
  AccountPage,
  CustomerInforPage,
  UsersPage,
  RolePage,
  InvoiceSignAndTemplatePage,
  InvoiceRegistrationPage,
  DataConfigPage,
  WidgetConfigPage,
  RelisoftPage,
  CustomersPage,
  OrdersPage,
  PriceListPage,
  SaleReportPage,
  SystemConfigPage,
  AutoSalePage,
  DevPage,
  InvoiceSupplierConnectionPage,
  PaymentMethodPage,
  InvoiceSummaryReportPage,
  ProductTablePage,
  IndustryPage,
  SalesRevenueReportPage,
  BusinessesPage,
  TaxPolicyPage,
  TaxSettingsPage,
  StorageReportPage,
  IndustryChoicePage,
  InvoiceReconciliationReportPage,
};

const renderRoutes = (menus) =>
  menus.map((item) => {
    if (item.children) {
      return (
        <Route
          key={item.path}
          path={item.path}
          element={
            pageMap[item.pageNo] ? (
              React.createElement(pageMap[item.pageNo])
            ) : (
              <></>
            )
          }
        >
          {renderRoutes(item.children)}
        </Route>
      );
    }
    return (
      <Route
        key={item.path}
        path={item.path}
        element={
          pageMap[item.pageNo] ? (
            React.createElement(pageMap[item.pageNo])
          ) : (
            <></>
          )
        }
      />
    );
  });

function DashboardLayout() {
  const [anchorElUser, setAnchorElUser] = useState(null);
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Lấy thông tin từ Redux store
  const user = useSelector((state) => state.auth.user);
  const customerInfor = useSelector((state) => state.auth.customerInfor);
  const customerParentInfor = useSelector(
    (state) => state.auth.customerParentInfor,
  );
  const isFirstLogin = useSelector((state) => state.auth.isFirstLogin);
  const roles = useSelector((state) => state.auth.roles) || null;
  const [loading, setLoading] = useState(true);

  // States cho Modal đổi mật khẩu
  const [openChangePasswordModal, setOpenChangePasswordModal] = useState(false);
  const oldPasswordRef = useRef(null);
  const newPasswordRef = useRef(null);
  const confirmPasswordRef = useRef(null);
  const [passwordChangeError, setPasswordChangeError] = useState("");
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState("");

  const [openBranchModal, setOpenBranchModal] = useState(false);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(null);
  const [openAuthModal, setOpenAuthModal] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");

  // States thông báo
  const [openDialogInvoiceTemplate, setOpenDialogInvoiceTemplate] =
    useState(false);
  const [notiInvoiceTemplate, setNotiInvoiceTemplate] = useState("");
  const [openDialogCustomerExpired, setOpenDialogCustomerExpired] =
    useState(false);
  const [notiExpiredDate, setNotiExpiredDate] = useState("");

  const [confirmDialogState, setConfirmDialogState] = useState({
    open: false,
    title: "",
    content: "",
    onConfirm: () => {},
    onClose: () => {},
  });
  const [informationDialogState, setInformationDialogState] = useState({
    open: false,
    title: "",
    content: "",
    confirmText: "",
    onConfirm: () => {},
  });

  const [menuItems, setMenuItems] = useState([]);
  const baseMenu = useRef([
    {
      text: "HÓA ĐƠN MÁY TÍNH TIỀN",
      path: "/hoa-don-may-tinh-tien",
      pageNo: "InvoicePage",
    },
    {
      text: "XỬ LÝ SAI SÓT",
      path: "/xu-ly-sai-sot",
      pageNo: "HandleInvoiceErrorPage",
    },
    {
      text: "BÁO CÁO THUẾ",
      path: "/bao-cao-thue",
      pageNo: "TaxReportPage",
      type: "dropdown",
      children: [
        // {
        //   text: "BÁO CÁO XHĐ",
        //   path: "/bao-cao-thue/bao-cao-xuat-hoa-don",
        //   pageNo: "InvoiceSummaryReportPage",
        // },
        // {
        //   text: "Sổ doanh thu bán hàng và dịch vụ",
        //   path: "/bao-cao-thue/so-doanh-thu-ban-hang-va-dich-vu",
        //   pageNo: "SalesRevenueReportPage",
        // },
        {
          text: "BC Hộ kinh doanh",
          path: "/bao-cao-thue/ho-kinh-doanh",
          pageNo: "SalesRevenueReportPage",
        },
        {
          text: "BC Công ty",
          path: "/bao-cao-thue/cong-ty",
          pageNo: "BusinessesPage",
        },
        {
          text: "BC đối chiếu",
          path: "/bao-cao-thue/bao-cao-doi-chieu",
          pageNo: "InvoiceReconciliationReportPage",
        },
        {
          text: "NGÀNH NGHỀ KINH DOANH",
          path: "/bao-cao-thue/nganh-nghe-kinh-doanh",
          pageNo: "IndustryChoicePage",
        },
        {
          text: "DANH MỤC NGÀNH NGHỀ",
          path: "/bao-cao-thue/danh-muc-nganh-nghe",
          pageNo: "IndustryPage",
        },
        {
          text: "Cài đặt thuế",
          path: "/bao-cao-thue/cai-dat-thue",
          pageNo: "TaxSettingsPage",
        },
        {
          text: "Lưu trữ báo cáo",
          path: "/bao-cao-thue/luu-tru-bao-cao",
          pageNo: "StorageReportPage",
        },
      ],
    },
    {
      text: "PHÂN TÍCH",
      path: "/phan-tich",
      pageNo: "AnalyzePage",
    },
    {
      text: "TÀI KHOẢN",
      path: "/tai-khoan",
      type: "dropdown",
      pageNo: "AccountPage",
      children: [
        {
          text: "THÔNG TIN",
          path: "/tai-khoan/thong-tin",
          pageNo: "CustomerInforPage",
        },
        {
          text: "NGƯỜI DÙNG",
          path: "/tai-khoan/nguoi-dung",
          pageNo: "UsersPage",
        },
        {
          text: "VAI TRÒ",
          path: "/tai-khoan/vai-tro",
          pageNo: "RolePage",
        },
        {
          text: "KÝ HIỆU VÀ MẪU",
          path: "/tai-khoan/ky-hieu-va-mau",
          pageNo: "InvoiceSignAndTemplatePage",
        },
        {
          text: "ĐĂNG KÝ HÓA ĐƠN",
          path: "/tai-khoan/dang-ky-hoa-don",
          pageNo: "InvoiceRegistrationPage",
        },
        {
          text: "CẤU HÌNH DỮ LIỆU",
          path: "/tai-khoan/cau-hinh-du-lieu",
          pageNo: "DataConfigPage",
        },
        {
          text: "CẤU HÌNH TIỆN ÍCH",
          path: "/tai-khoan/cau-hinh-tien-ich",
          pageNo: "WidgetConfigPage",
        },
      ],
    },
    {
      text: "RELISOFT",
      path: "/relisoft",
      type: "dropdown",
      pageNo: "RelisoftPage",
      children: [
        {
          text: "KHÁCH HÀNG",
          path: "/relisoft/khach-hang",
          pageNo: "CustomersPage",
        },
        {
          text: "ĐƠN ĐẶT HÀNG",
          path: "/relisoft/don-dat-hang",
          pageNo: "OrdersPage",
        },
        {
          text: "BẢNG GIÁ",
          path: "/relisoft/bang-gia",
          pageNo: "PriceListPage",
        },
        {
          text: "BÁO CÁO SALE",
          path: "/relisoft/bao-cao-sale",
          pageNo: "SaleReportPage",
        },
        {
          text: "CẤU HÌNH SYSTEM",
          path: "/relisoft/cau-hinh-system",
          pageNo: "SystemConfigPage",
        },
        {
          text: "AUTOSALE",
          path: "/relisoft/autosale",
          pageNo: "AutoSalePage",
        },
      ],
    },
    {
      text: "DEV",
      path: "/dev",
      type: "dropdown",
      pageNo: "DevPage",
      children: [
        {
          text: "InvoiceSupplierConnectionPage",
          path: "/dev/api-hoa-don",
          pageNo: "InvoiceSupplierConnectionPage",
        },
        {
          text: "PaymentMethodPage",
          path: "/dev/hinh-thuc-thanh-toan",
          pageNo: "PaymentMethodPage",
        },
      ],
    },
  ]).current;

  useEffect(() => {
    const handleNotifications = async () => {
      setLoading(true);
      try {
        const resUser = await userApi.GetUserInfor(
          customerInfor?.CusId,
          user?.UsId,
        );
        if (resUser.success) {
          dispatch(
            setUserData({
              user: resUser.data.userInfor,
              roles: resUser.data.userRoles,
              customerInfor: resUser.data.customerInfor,
              customerParentInfor: resUser.data.customerParentInfor,
            }),
          );
          if (isFirstLogin) {
            if (customerInfor.CusIsAdmin === false) {
              let quantityRemain =
                resUser.data.customerInfor.CusInvoiceQuantityRemain;
              if (resUser.data.customerParentInfor != null)
                quantityRemain =
                  resUser.data.customerParentInfor.CusInvoiceQuantityRemain;

              if (quantityRemain <= 500) {
                setOpenDialogInvoiceTemplate(true);
                setNotiInvoiceTemplate(
                  "Hệ thống chỉ còn " +
                    quantityRemain +
                    " số hóa đơn điện tử có thể xuất cho khách hàng, hãy nhấn “Đăng ký mua hóa đơn” để bổ sung số hóa đơn, việc đăng ký hoàn toàn tự động.",
                );
              }
              let expiredDate = dayjs(
                resUser.data.customerInfor.CusExpiredDate,
              );
              if (resUser.data.customerParentInfor != null)
                expiredDate = dayjs(
                  resUser.data.customerParentInfor.CusExpiredDate,
                );
              let diffDays = expiredDate.diff(dayjs(), "day");
              if (diffDays <= 30) {
                setOpenDialogCustomerExpired(true);
                if (diffDays <= 0) {
                  setNotiExpiredDate(
                    "Gói phần mềm Xuất hóa đơn từ máy tính tiền nhanh và tự động, đã hết hạn sử dụng, hãy “Gia hạn phần mềm” để tiếp tục sử dụng dịch vụ. Việc gia hạn hoàn toàn tự động ngay sau khi thanh toán",
                  );
                } else {
                  setNotiExpiredDate(
                    "Gói phần mềm Xuất hóa đơn từ máy tính tiền nhanh và tự động, chỉ còn " +
                      diffDays +
                      " ngày sử dụng, hãy “Gia hạn phần mềm” để tránh gián đoạn dịch vụ. Việc gia hạn hoàn toàn tự động ngay sau khi thanh toán",
                  );
                }
              }
              dispatch(clearFirstLogin());
            }
            if (user.UsEmail !== "admini") {
              invoiceApi.getInvoicesInfor(
                customerInfor?.CusId,
                dayjs().subtract(1, "day"),
                dayjs(),
              );
            }
          }
        }
      } finally {
        setLoading(false);
      }
    };
    handleNotifications();
  }, [isFirstLogin, dispatch, customerInfor?.CusId, user?.UsId]);

  useEffect(() => {
    const filtered = baseMenu.filter((item) => {
      // 1. Nếu là Admin, cho phép tất cả các item trong baseMenu
      if (customerInfor?.CusIsAdmin || item?.isTest === true) return true;

      // 2. Kiểm tra xem item có nằm trong danh sách cấm không
      const isForbiddenPath = item.path === "/relisoft" || item.path === "/dev";

      // 3. Kiểm tra xem user có quyền truy cập page này trong mảng roles không
      const hasPermission = roles.some(
        (role) =>
          role.PvNo === item.pageNo &&
          role.CtrNo === "viewPage" &&
          role.IsApproved === true,
      );

      // Điều kiện: KHÔNG PHẢI path cấm VÀ PHẢI CÓ quyền
      return !isForbiddenPath && hasPermission;
    });
    setMenuItems(filtered);
  }, [roles, baseMenu, customerInfor?.CusIsAdmin]);

  const [tabValue, setTabValue] = useState(0);
  useEffect(() => {
    setTabValue(() => {
      let i = 0;
      menuItems.map((item, index) => {
        if (
          location.pathname === item.path ||
          (item.path !== "/" && location.pathname.startsWith(item.path + "/"))
        )
          i = index;
      });
      return i;
    });
  }, [menuItems]);

  const handleChangeTab = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogoutClick = async () => {
    const response = await userApi.logout();
    if (response.success) {
      handleCloseUserMenu();
      dispatch(logout());
    }
  };
  const handleCloseChangePasswordModal = () => {
    setOpenChangePasswordModal(false);
    if (oldPasswordRef.current) oldPasswordRef.current.value = "";
    if (newPasswordRef.current) newPasswordRef.current.value = "";
    if (confirmPasswordRef.current) confirmPasswordRef.current.value = "";
  };

  const handleChangePasswordSubmit = async (event) => {
    event.preventDefault();
    setPasswordChangeError("");
    setPasswordChangeSuccess("");

    const oldPassword = oldPasswordRef.current.value;
    const newPassword = newPasswordRef.current.value;
    const confirmNewPassword = confirmPasswordRef.current.value;

    if (!oldPassword || !newPassword || !confirmNewPassword) {
      setPasswordChangeError("Vui lòng điền đầy đủ tất cả thông tin.");
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setPasswordChangeError("Mật khẩu mới và xác nhận mật khẩu không khớp.");
      return;
    }

    if (newPassword.length < 6) {
      setPasswordChangeError("Mật khẩu mới phải có ít nhất 6 ký tự.");
      return;
    }
    try {
      const response = await userApi.changePassword(
        user.UsId,
        oldPassword,
        newPassword,
      );
      if (response.success) {
        setPasswordChangeSuccess(
          response.message || "Đổi mật khẩu thành công!",
        );
        setTimeout(() => {
          handleCloseChangePasswordModal();
        }, 1500);
      } else {
        setPasswordChangeError(response.message || "Đổi mật khẩu thất bại.");
      }
    } catch (error) {
      setPasswordChangeError(error.toString() || "Đổi mật khẩu thất bại.");
    }
  };

  const handleChangePass = async () => {
    try {
      const response = await userApi.SendEmailChangePassword(
        user.UsId,
        customerInfor?.CusId,
      );
      if (response.success) {
        setInformationDialogState({
          open: true,
          title: "",
          content:
            "Chúng tôi đã gửi liên kết để đặt lại mật khẩu đến email của bạn. Vui lòng kiểm tra email.",
          onConfirm: () => {
            setInformationDialogState({
              ...informationDialogState,
              open: false,
            });
            setConfirmDialogState({
              ...confirmDialogState,
              open: false,
            });
          },
          confirmText: "Đóng",
        });
      } else {
        toast.error(response.message || "Đổi mật khẩu thất bại.");
      }
    } catch (error) {
      toast.error(error.toString() || "Đổi mật khẩu thất bại.");
    }
  };
  const { nestedData, parentIds } = useMemo(() => {
    const map = {};
    const roots = [];
    const parents = [];

    branches.forEach((item) => {
      map[item.CusId] = {
        ...item,
        title: item.CusCompanyName + " - MST: " + item.CusTaxId,
        key: item.CusId,
        children: [],
      };
    });

    branches.forEach((item) => {
      if (item.customerParentId && map[item.customerParentId]) {
        map[item.customerParentId].children.push(map[item.CusId]);
        // Nếu item này là cha của ai đó, CusId của nó cần được lưu để expand
        if (!parents.includes(item.customerParentId))
          parents.push(item.customerParentId);
      } else {
        roots.push(map[item.CusId]);
      }
    });

    return { nestedData: roots, parentIds: parents };
  }, [branches]);

  const btnChangeBranch_Click = async () => {
    try {
      const response = await customerApi.getCustomerByUser(user.UsId);
      if (response.success) {
        setBranches(response.data);
        setOpenBranchModal(true);
        handleCloseUserMenu();
      }
    } catch (error) {
      toast.error(error.toString() || "Thất bại");
    }
  };
  const handleChangeBranch = async () => {
    try {
      let cusInfor = branches.find((item) => item.CusId === branchId);
      const resUser = await userApi.login({
        taxid: cusInfor.CusTaxId,
        username: user.UsEmail,
        password: confirmPassword,
      });
      if (resUser.success) {
        dispatch(
          setUserData({
            user: resUser.data.userInfor,
            roles: resUser.data.userRoles,
            customerInfor: resUser.data.customerInfor,
            customerParentInfor: resUser.data.customerParentInfor,
          }),
        );
        dispatch(changeBranch());
        setOpenAuthModal(false);
        setOpenBranchModal(false);
        setBranchId(null);
        setConfirmPassword("");
        navigate("/hoa-don-may-tinh-tien");
      } else {
        toast.error(resUser.message || "Thất bại");
      }
    } catch (error) {
      toast.error(error.toString() || "Thất bại");
    }
  };

  if (loading) return <></>;
  return (
    <Box sx={{ display: "flex", height: "100%" }}>
      <CssBaseline />
      <AppBarStyled position="fixed" sx={{ background: "#D3E3F8" }}>
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: { xs: 1, sm: 2 },
          }}
        >
          {/* Logo */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              mr: 1,
              cursor: "pointer",
            }}
            onClick={() => navigate("/hoa-don-may-tinh-tien")}
          >
            <img
              src="/Relisoft1024_ngang.svg"
              alt="Logo"
              style={{ height: "40px" }}
            />
          </Box>

          {/* Menu Tabs trên cùng hàng với Logo */}
          <Tabs
            value={tabValue}
            onChange={handleChangeTab}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              flexGrow: 1,
              minWidth: 0,
              minHeight: 56,
              "& .MuiTabs-scrollButtons.Mui-disabled": {
                opacity: 0.3,
              },
              "& .MuiTab-root": {
                minHeight: 56,
                fontWeight: 600,
                fontSize: "0.85rem",
                color: "#333333",
                textTransform: "none",
                px: 1.5,
                transition: "all 0.2s ease",
                "&:hover": {
                  color: "#1565c0",
                  backgroundColor: "rgba(21, 101, 192, 0.08)",
                },
                "&.Mui-selected": {
                  color: "#1565c0",
                  fontWeight: "bold",
                },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#1565c0",
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
            }}
          >
            {menuItems.map((item, index) => {
              return (
                <Tab
                  key={index}
                  label={item.text}
                  value={index}
                  onClick={() => {
                    navigate(item.path);
                  }}
                />
              );
            })}
          </Tabs>

          {/* Thông tin doanh nghiệp & User như cũ */}
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ flexShrink: 0 }}
          >
            <Box>
              <Typography variant="body2" fontWeight="bold" align="right">
                {customerInfor?.CusCompanyName +
                  " - MST: " +
                  customerInfor?.CusTaxId}
              </Typography>
              <Typography variant="body2" fontWeight="bold" align="right">
                {user?.UsFullName || user?.UsEmail}
              </Typography>
            </Box>

            <Divider orientation="vertical" flexItem />
            <Tooltip title="Tùy chọn người dùng">
              <Button
                onClick={handleOpenUserMenu}
                sx={{ p: 0, textTransform: "none", color: "inherit" }}
              >
                <Avatar alt={user?.UsEmail} />
              </Button>
            </Tooltip>
            <Menu
              sx={{ mt: "45px" }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              <MenuItem>
                <ListItemIcon>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{user.UsFullName}</ListItemText>
              </MenuItem>
              <MenuItem>
                <ListItemIcon>
                  <MailIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{user.UsEmail}</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => btnChangeBranch_Click()}>
                <ListItemIcon>
                  <SpaceDashboardIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Chuyển không gian làm việc</ListItemText>
              </MenuItem>
              <MenuItem
                disabled={user.UsEmail === "admini"}
                onClick={() =>
                  setConfirmDialogState({
                    open: true,
                    title: "Đổi mật khẩu",
                    content: "Bạn chắc chắn muốn đổi mật khẩu?",
                    onConfirm: () => handleChangePass(),
                    onClose: () =>
                      setConfirmDialogState({
                        ...confirmDialogState,
                        open: false,
                      }),
                  })
                }
              >
                <ListItemIcon>
                  <KeyIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Đổi mật khẩu</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleLogoutClick} sx={{ color: "red" }}>
                <ListItemIcon>
                  <ExitToAppIcon fontSize="small" sx={{ color: "red" }} />
                </ListItemIcon>
                <ListItemText>Đăng xuất</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBarStyled>
      <Box
        sx={{
          flexGrow: 1,
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Toolbar />

        <Box
          sx={{
            flexGrow: 1,
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Main>
            <Routes>
              <Route
                path="/"
                element={<Navigate to="/hoa-don-may-tinh-tien" replace />}
              />
              {renderRoutes(baseMenu)}
              <Route
                path="*"
                // element={
                //   <Typography variant="h4">
                //     404 - Trang không tìm thấy
                //   </Typography>
                // }
              />
            </Routes>
          </Main>
        </Box>
      </Box>

      {/* Modal Đổi mật khẩu */}
      <Dialog
        open={openChangePasswordModal}
        onClose={handleCloseChangePasswordModal}
        maxWidth="xs"
      >
        <Box component="form" onSubmit={handleChangePasswordSubmit}>
          <DialogTitle>Đổi mật khẩu</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              id="old-password"
              label="Mật khẩu cũ"
              type="password"
              fullWidth
              variant="outlined"
              inputRef={oldPasswordRef}
              sx={{ mb: 2 }}
              size="medium"
            />
            <TextField
              id="new-password"
              label="Mật khẩu mới"
              type="password"
              fullWidth
              variant="outlined"
              inputRef={newPasswordRef}
              sx={{ mb: 2 }}
              size="medium"
            />
            <TextField
              id="confirm-new-password"
              label="Xác nhận mật khẩu mới"
              type="password"
              fullWidth
              variant="outlined"
              inputRef={confirmPasswordRef}
              sx={{ mb: 2 }}
              size="medium"
            />
            {passwordChangeError && (
              <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                {passwordChangeError}
              </Alert>
            )}
            {passwordChangeSuccess && (
              <Alert severity="success" sx={{ mt: 1, mb: 1 }}>
                {passwordChangeSuccess}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseChangePasswordModal} color="primary">
              Hủy
            </Button>
            <Button type="submit" variant="contained" color="primary">
              Xác nhận
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
      <ConfirmDialog
        open={confirmDialogState.open}
        title={confirmDialogState.title}
        content={confirmDialogState.content}
        onConfirm={confirmDialogState.onConfirm}
        onClose={confirmDialogState.onClose}
      />
      <InformationDialog
        open={informationDialogState.open}
        title={informationDialogState.title}
        content={informationDialogState.content}
        onConfirm={informationDialogState.onConfirm}
        confirmText={informationDialogState.confirmText}
      />
      <Dialog open={openDialogInvoiceTemplate}>
        <DialogTitle>Thông báo</DialogTitle>
        <DialogContent>{notiInvoiceTemplate}</DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setOpenDialogInvoiceTemplate(false)}
          >
            Đóng
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openDialogCustomerExpired}>
        <DialogTitle>Thông báo</DialogTitle>
        <DialogContent>{notiExpiredDate}</DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setOpenDialogCustomerExpired(false)}
          >
            Đóng
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openBranchModal} fullWidth maxWidth="sm">
        <DialogTitle>Không gian làm việc của tôi</DialogTitle>
        <DialogContent dividers sx={{ minHeight: "200px" }}>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              py={5}
            >
              <CircularProgress />
            </Box>
          ) : (
            <ConfigProvider
              theme={{
                components: {
                  Tree: {
                    nodeSelectedBg: "#bae7ff", // Màu nền khi click chọn (nhạt)
                    controlItemBgHover: "#e6f7ff", // Màu nền khi di chuột qua (hover)
                  },
                },
              }}
            >
              <Tree
                showLine
                switcherIcon={<DownOutlined />}
                defaultExpandedKeys={parentIds}
                defaultSelectedKeys={
                  customerInfor?.CusId ? [customerInfor.CusId] : []
                }
                onSelect={(selectedKeys, info) => {
                  setBranchId(selectedKeys[0]);
                }}
                treeData={nestedData}
              />
            </ConfigProvider>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => {
              setBranchId(null);
              setOpenBranchModal(false);
            }}
          >
            Đóng
          </Button>
          <Button
            variant="contained"
            disabled={!branchId}
            color="primary"
            onClick={() => {
              if (branchId === null) {
                toast.error("Vui lòng chọn không gian làm việc");
                return;
              }
              if (branchId === customerInfor?.CusId) {
                return;
              }
              setOpenAuthModal(true);
            }}
          >
            Chuyển
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openAuthModal} fullWidth maxWidth="sm">
        <DialogTitle>Vui lòng xác nhận lại mật khẩu</DialogTitle>
        <Divider />
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="confirm-password"
            label="Xác nhận mật khẩu"
            type="password"
            fullWidth
            variant="outlined"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => {
              setOpenAuthModal(false);
              setConfirmPassword("");
            }}
          >
            Đóng
          </Button>
          <Button
            variant="contained"
            disabled={!confirmPassword}
            color="primary"
            onClick={() => handleChangeBranch()}
          >
            Xác nhận
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DashboardLayout;
